import React, { useState } from "react";
import { Loader2, LockKeyhole, UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PartnerForm from "@/components/forms/PartnerForm";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { phpApi } from "@/integrations/php/client";

const PartnerRegistration: React.FC = () => {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);

  // Server-side password verification
  const verifyPassword = async () => {
    setIsChecking(true);
    setError(null);

    try {
      const { data, error: invokeError } = await phpApi.functions.invoke(
        "verify-partner-password",
        { body: { password } }
      );

      if (invokeError) {
        setError("Σφάλμα επικοινωνίας. Δοκιμάστε ξανά αργότερα.");
        toast({
          title: "Σφάλμα",
          description: "Πρόβλημα κατά την επαλήθευση του κωδικού.",
          variant: "destructive",
        });
        return;
      }

      if (data?.success) {
        setAuthenticated(true);
        toast({
          title: "Επιτυχής επαλήθευση",
          description: "Μπορείτε τώρα να συμπληρώσετε τη φόρμα εγγραφής συνεργάτη.",
        });
      } else {
        setError(data?.message || "Λάθος κωδικός. Παρακαλώ δοκιμάστε ξανά.");
        toast({
          title: "Λάθος κωδικός",
          description: data?.message || "Ο κωδικός που εισάγατε είναι λανθασμένος.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Password verification error:", err);
      setError("Σφάλμα επικοινωνίας. Δοκιμάστε ξανά αργότερα.");
      toast({
        title: "Σφάλμα",
        description: "Πρόβλημα κατά την επαλήθευση του κωδικού.",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  // Success handler no longer redirects
  const handleSuccess = () => {
    toast({
      title: "Επιτυχής εγγραφή!",
      description: "Η εγγραφή σας ολοκληρώθηκε επιτυχώς. Θα ενημερωθείτε σύντομα.",
    });
    setShowThankYou(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25 mx-auto mb-4">
            <UserPlus className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Εγγραφή Νέου Συνεργάτη</h1>
          <p className="text-slate-500">
            Συμπληρώστε τη φόρμα για να εγγραφείτε ως συνεργάτης
          </p>
        </div>

        {!authenticated ? (
          <Card className="shadow-xl border-0 animate-fade-in max-w-md mx-auto">
            <CardHeader className="text-center pb-2">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-slate-100 mx-auto mb-3">
                <LockKeyhole className="h-6 w-6 text-slate-600" />
              </div>
              <CardTitle className="text-xl text-slate-900">Επαλήθευση Κωδικού</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Εισάγετε τον κωδικό που σας δόθηκε από τον διαχειριστή</p>
            </CardHeader>
            <CardContent className="pt-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verifyPassword();
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Κωδικός Πρόσβασης</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Εισάγετε τον κωδικό που σας δόθηκε"
                    className={`h-11 bg-slate-50 border-slate-200 focus:border-blue-500 ${error ? "border-red-400" : ""}`}
                  />
                  {error && <p className="text-sm text-red-500">{error}</p>}
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25"
                  disabled={isChecking || !password}
                >
                  {isChecking ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Επαλήθευση...
                    </>
                  ) : (
                    "Συνέχεια"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <div className="animate-fade-in space-y-4">
            {showThankYou && (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-800">Εγγραφή επιτυχής!</p>
                  <p className="text-sm text-emerald-700">Η αίτησή σας υποβλήθηκε και θα επικοινωνήσουμε μαζί σας σύντομα.</p>
                </div>
              </div>
            )}

            <Card className="shadow-xl border-0">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-2xl text-slate-900">Φόρμα Εγγραφής Συνεργάτη</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className={showThankYou ? "opacity-40 pointer-events-none" : ""}>
                  <PartnerForm onSuccess={handleSuccess} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerRegistration;
